'use client';

import { useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check, FileText, BarChart2, Filter, Hash, FileInput, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { keywordDensityContent } from '../../data/keyword-density-content';
import Link from 'next/link';

type WordFrequency = {
    word: string;
    count: number;
    density: number;
};

type NGram = {
    phrase: string;
    count: number;
    density: number;
};

// Common English stop words to filter out
const STOP_WORDS = new Set([
    'a', 'an', 'the', 'and', 'or', 'but', 'if', 'because', 'as', 'what', 'which',
    'this', 'that', 'these', 'those', 'then', 'just', 'so', 'than', 'such', 'both',
    'through', 'about', 'for', 'is', 'of', 'while', 'during', 'to', 'in', 'on',
    'at', 'by', 'from', 'up', 'down', 'under', 'over', 'again', 'further', 'then',
    'once', 'here', 'there', 'when', 'where', 'why', 'how', 'all', 'any', 'both',
    'each', 'few', 'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not',
    'only', 'own', 'same', 'so', 'than', 'too', 'very', 'can', 'will', 'just',
    'don', 'should', 'now', 'd', 'll', 'm', 'o', 're', 've', 'y', 'ain', 'aren',
    'couldn', 'didn', 'doesn', 'hadn', 'hasn', 'haven', 'isn', 'ma', 'mightn',
    'mustn', 'needn', 'shan', 'shouldn', 'wasn', 'weren', 'won', 'wouldn'
]);

export default function KeywordDensityAnalyzer() {
    const [text, setText] = useState('');
    const [copied, setCopied] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [minWordLength, setMinWordLength] = useState(3);
    const [minFrequency, setMinFrequency] = useState(2);
    const [filterStopWords, setFilterStopWords] = useState(true);
    const [customStopWords, setCustomStopWords] = useState('');
    const [ngramSize, setNgramSize] = useState<number>(2);
    const [showNgrams, setShowNgrams] = useState(false);
    const { toast } = useToast();

    // Combine default and custom stop words
    const allStopWords = useMemo(() => {
        const customWords = customStopWords
            .toLowerCase()
            .split(/[\s,]+/)
            .filter(word => word.length > 0);

        return new Set([...STOP_WORDS, ...customWords]);
    }, [customStopWords]);

    // Calculate word frequencies and densities
    const wordFrequencies = useMemo(() => {
        if (!text.trim()) return [];

        const words = text
            .toLowerCase()
            .replace(/[^\w\s]|_/g, '') // Remove punctuation
            .replace(/\s+/g, ' ')      // Normalize spaces
            .split(' ')
            .filter(word =>
                word.length >= minWordLength &&
                (!filterStopWords || !allStopWords.has(word))
            );

        const totalWords = words.length;
        if (totalWords === 0) return [];

        const frequencyMap = words.reduce((acc, word) => {
            acc[word] = (acc[word] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        return Object.entries(frequencyMap)
            .filter(([_, count]) => count >= minFrequency)
            .map(([word, count]) => ({
                word,
                count,
                density: Number(((count / totalWords) * 100).toFixed(2))
            }))
            .sort((a, b) => b.count - a.count);
    }, [text, minWordLength, filterStopWords, allStopWords, minFrequency]);

    // Calculate n-grams
    const ngrams = useMemo(() => {
        if (!showNgrams || !text.trim()) return [];

        const words = text
            .toLowerCase()
            .replace(/[^\w\s]|_/g, '')
            .split(/\s+/)
            .filter(word => !filterStopWords || !allStopWords.has(word));

        if (words.length < ngramSize) return [];

        const ngramMap: Record<string, number> = {};
        const totalNgrams = words.length - ngramSize + 1;

        for (let i = 0; i <= words.length - ngramSize; i++) {
            const ngram = words.slice(i, i + ngramSize).join(' ');
            ngramMap[ngram] = (ngramMap[ngram] || 0) + 1;
        }

        return Object.entries(ngramMap)
            .filter(([_, count]) => count >= minFrequency)
            .map(([phrase, count]) => ({
                phrase,
                count,
                density: Number(((count / totalNgrams) * 100).toFixed(3))
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 20); // Limit to top 20 n-grams
    }, [text, showNgrams, ngramSize, filterStopWords, allStopWords, minFrequency]);

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setText(e.target.value);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const fileContent = event.target?.result as string;
            setText(fileContent);
        };
        reader.readAsText(file);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const clearText = () => {
        setText('');
    };

    const totalWords = useMemo(() => {
        return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
    }, [text]);

    const uniqueWords = useMemo(() => {
        const words = text
            .toLowerCase()
            .replace(/[^\w\s]|_/g, '')
            .split(/\s+/);
        return new Set(words).size;
    }, [text]);

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Keyword Density Analyzer">
                Analyze keyword density in your text with our free online tool. Optimize your content for SEO by checking keyword frequency and distribution.
            </ToolPageIntro>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <div>
                                    <CardTitle>Text Input</CardTitle>
                                    <CardDescription>Paste your content to analyze keyword density</CardDescription>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        className="text-xs"
                                    >
                                        {showAdvanced ? 'Hide Options' : 'Show Options'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={text}
                                    onChange={handleTextChange}
                                    placeholder="Paste your article, blog post, or other text content here..."
                                    className="min-h-[300px] w-full text-base"
                                    rows={10}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={copyToClipboard}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Copy to clipboard"
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearText}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Clear text"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="relative">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        asChild
                                        className="cursor-pointer"
                                    >
                                        <Label htmlFor="file-upload" className="cursor-pointer">
                                            <FileInput className="h-4 w-4 mr-2" />
                                            Upload File
                                            <input
                                                id="file-upload"
                                                type="file"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                accept=".txt,.md,.doc,.docx,.pdf"
                                            />
                                        </Label>
                                    </Button>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {totalWords} words • {uniqueWords} unique words
                                </div>
                            </div>

                            {showAdvanced && (
                                <div className="pt-4 border-t mt-4 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="min-word-length">Minimum word length: {minWordLength}</Label>
                                            <Slider
                                                id="min-word-length"
                                                value={[minWordLength]}
                                                onValueChange={([value]) => setMinWordLength(value)}
                                                min={1}
                                                max={10}
                                                step={1}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="min-frequency">Minimum frequency: {minFrequency}</Label>
                                            <Slider
                                                id="min-frequency"
                                                value={[minFrequency]}
                                                onValueChange={([value]) => setMinFrequency(value)}
                                                min={1}
                                                max={10}
                                                step={1}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            id="filter-stop-words"
                                            checked={filterStopWords}
                                            onCheckedChange={setFilterStopWords}
                                        />
                                        <Label htmlFor="filter-stop-words">Filter common stop words</Label>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="custom-stop-words">Custom stop words (comma separated)</Label>
                                        <Input
                                            id="custom-stop-words"
                                            value={customStopWords}
                                            onChange={(e) => setCustomStopWords(e.target.value)}
                                            placeholder="e.g., company, product, brand"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                id="show-ngrams"
                                                checked={showNgrams}
                                                onCheckedChange={setShowNgrams}
                                            />
                                            <Label htmlFor="show-ngrams">Show {ngramSize}-word phrases</Label>
                                        </div>
                                        {showNgrams && (
                                            <div className="pl-6 space-y-2">
                                                <Label htmlFor="ngram-size">Phrase length: {ngramSize} words</Label>
                                                <Slider
                                                    id="ngram-size"
                                                    value={[ngramSize]}
                                                    onValueChange={([value]) => setNgramSize(value)}
                                                    min={2}
                                                    max={5}
                                                    step={1}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Keyword Density</CardTitle>
                            <CardDescription>Top keywords by frequency</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {wordFrequencies.length > 0 ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <StatCard
                                            label="Total Words"
                                            value={totalWords.toLocaleString()}
                                            icon={<FileText className="h-4 w-4" />}
                                        />
                                        <StatCard
                                            label="Unique Words"
                                            value={uniqueWords.toLocaleString()}
                                            icon={<Hash className="h-4 w-4" />}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <h3 className="font-medium flex items-center">
                                            <Filter className="h-4 w-4 mr-2" />
                                            Top Keywords
                                        </h3>
                                        <div className="border rounded-lg overflow-hidden">
                                            <table className="w-full text-sm">
                                                <thead className="bg-muted/50">
                                                    <tr>
                                                        <th className="text-left p-2 font-medium">Keyword</th>
                                                        <th className="text-right p-2 font-medium">Count</th>
                                                        <th className="text-right p-2 font-medium">Density</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y">
                                                    {wordFrequencies.slice(0, 30).map((item, index) => (
                                                        <tr key={index} className="hover:bg-muted/50">
                                                            <td className="p-2 font-medium">{item.word}</td>
                                                            <td className="p-2 text-right">{item.count}</td>
                                                            <td className="p-2 text-right">{item.density}%</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {showNgrams && ngrams.length > 0 && (
                                        <div className="space-y-2">
                                            <h3 className="font-medium flex items-center">
                                                <Filter className="h-4 w-4 mr-2" />
                                                Top {ngramSize}-Word Phrases
                                            </h3>
                                            <div className="border rounded-lg overflow-hidden">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-muted/50">
                                                        <tr>
                                                            <th className="text-left p-2 font-medium">Phrase</th>
                                                            <th className="text-right p-2 font-medium">Count</th>
                                                            <th className="text-right p-2 font-medium">Density</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y">
                                                        {ngrams.map((ngram, index) => (
                                                            <tr key={index} className="hover:bg-muted/50">
                                                                <td className="p-2 font-medium">{ngram.phrase}</td>
                                                                <td className="p-2 text-right">{ngram.count}</td>
                                                                <td className="p-2 text-right">{ngram.density}%</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    {text ? 'No keywords match the current filters' : 'Enter or upload text to analyze keyword density'}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>About Keyword Density</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm text-muted-foreground">
                            <p>
                                Keyword density is the percentage of times a keyword appears on a page compared to the total number of words.
                            </p>
                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">Best Practices:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Aim for 1-2% keyword density for main keywords</li>
                                    <li>Use related terms and synonyms (LSI keywords)</li>
                                    <li>Focus on natural, readable content first</li>
                                    <li>Use keywords in headings and subheadings</li>
                                </ul>
                            </div>
                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">Tips:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Longer content can naturally include more keywords</li>
                                    <li>Use variations of your main keywords</li>
                                    <li>Check competitor content for keyword ideas</li>
                                    <li>Update and optimize existing content regularly</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={keywordDensityContent.title}
                    description={keywordDensityContent.description}
                    content={keywordDensityContent.content}
                    faqs={keywordDensityContent.faqs}
                    relatedTools={keywordDensityContent.relatedTools}
                />
            </div>
        </div>
    );
}

function StatCard({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
    return (
        <div className="border rounded-lg p-3">
            <div className="flex items-center space-x-2 text-muted-foreground">
                {icon}
                <span className="text-sm">{label}</span>
            </div>
            <p className="mt-1 text-xl font-semibold">{value}</p>
        </div>
    );
}
